// src/pages/LittleAuthors.js

import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';

const useFadeInEffect = () => {
  const animatedElements = useRef([]);

  useEffect(() => {
    const elementsToObserve = animatedElements.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible', 'fade-in-fun');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    );

    if (elementsToObserve) {
      elementsToObserve.forEach((el) => {
        if (el) observer.observe(el);
      });
    }
    return () => {
      if (elementsToObserve) {
        elementsToObserve.forEach((el) => {
          if (el) observer.unobserve(el);
        });
      }
    };
  }, []);

  const addToRefs = (el) => {
    if (el && !animatedElements.current.includes(el)) {
      animatedElements.current.push(el);
    }
  };
  return addToRefs;
};

const LittleAuthors = () => {
  const addToRefs = useFadeInEffect();

  const authors = [
    { 
      book: "A Message from the Future", 
      img: "/schoolproject/SchoolProjectsLittleHibba1png.png", 
      color: "#4facfe",
      tag: "Program Debut!"
    },
    { 
      book: "The Responding Book", 
      img: "/schoolproject/SchoolProjectsLittleHibba2.png", 
      color: "#ff758c",
      tag: "Visionary Mind!"
    },
    { 
      book: "The Day Everything Froze", 
      img: "/schoolproject/SchoolProjectsLittleHibba3.png", 
      color: "#a18cd1",
      tag: "Interactive Adventure!"
    },
    { 
      book: "The Night Dragon", 
      img: "/schoolproject/SchoolProjectsLittleHibba4.png", 
      color: "#f6d365",
      tag: "Icy-Cool Tales!"
    },
    { 
      book: "The Battle of Badr", 
      img: "/schoolproject/SchoolProjectsLittleHibba5.png", 
      color: "#43e97b",
      tag: "Guardian of the Dark!"
    },
    { 
      book: "The Creature in the Classroom", 
      img: "/schoolproject/SchoolProjectsLittleHibba6.png", 
      color: "#fa709a",
      tag: "Historic Inspiration!"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Little Authors | Little Hibba</title>
        <meta name="description" content="Meet our winning little authors from the Author to Entrepreneur program." />
      </Helmet>

      <style>{`
        /* Fun & School-Themed CSS */
        .bg-school-pattern {
          background-color: #fdfbfb;
          background-image: 
            linear-gradient(90deg, transparent 79px, #abced4 79px, #abced4 81px, transparent 81px),
            linear-gradient(#eee .1em, transparent .1em);
          background-size: 100% 1.2em;
        }
        
        .bg-chalkboard {
          background-color: #2c3e50;
          background-image: radial-gradient(#34495e 15%, transparent 16%), radial-gradient(#34495e 15%, transparent 16%);
          background-size: 30px 30px;
          background-position: 0 0, 15px 15px;
          color: white;
        }

        .fade-in-fun { 
          animation: fadeInFun 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; 
        }
        
        @keyframes fadeInFun { 
          from { opacity: 0; transform: translateY(40px) scale(0.9); filter: blur(4px); } 
          to { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); } 
        }
        
        .invisible-start { opacity: 0; }
        
        .fun-heading { 
          font-family: 'Comic Sans MS', 'Chalkboard SE', sans-serif; 
          font-weight: 900;
          letter-spacing: 1px;
        }

        .text-reading { 
          font-size: 1.2rem; 
          line-height: 1.8; 
          color: #444; 
          font-family: 'Quicksand', sans-serif;
        }
        
        .chalk-text {
          font-family: 'Comic Sans MS', 'Chalkboard SE', sans-serif; 
          color: #f1f2f6;
        }

        .kid-card {
          border-radius: 20px;
          border: 4px dashed;
          background: #fff;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          box-shadow: 5px 5px 15px rgba(0,0,0,0.05);
        }

        .kid-card:hover {
          transform: translateY(-10px) rotate(2deg);
          box-shadow: 8px 8px 20px rgba(0,0,0,0.15);
        }

        .kid-img {
          width: 100%;
          border-bottom: 4px solid #f1f2f6;
          object-fit: cover;
          aspect-ratio: 3/4;
        }

        .badge-fun {
          background-color: #ff6b81;
          color: white;
          font-weight: bold;
          padding: 8px 20px;
          border-radius: 30px;
          font-size: 0.9rem;
          text-transform: uppercase;
          letter-spacing: 1px;
          box-shadow: 2px 2px 0px rgba(0,0,0,0.1);
        }

        .hero-section {
          padding-top: max(100px, 10vh);
          padding-bottom: 80px;
          border-bottom: 8px dashed #abced4;
        }
      `}</style>

      {/* --- SECTION 1: HERO --- */}
      <section className="hero-section bg-school-pattern">
        <Container>
          <Row className="align-items-center justify-content-center text-center">
            <Col lg={9} className="invisible-start" ref={addToRefs}>
              <span className="badge-fun mb-4 d-inline-block" style={{ backgroundColor: '#ff4757' }}>
                Little Authors, Big Entrepreneurs Program
              </span>
              <h1 className="display-3 fun-heading mb-4" style={{ color: '#2f3542' }}>
                Meet our winning <span style={{ color: '#1e90ff' }}>little Authors!</span>
              </h1>
              <p className="text-reading mx-auto mb-4" style={{ maxWidth: '800px', fontSize: '1.3rem' }}>
                We are absolutely thrilled to showcase the incredible talent from <strong>Cardiff Muslim Primary School</strong>. 
                These young visionaries participated in our 'Author to Entrepreneur' program, writing, illustrating, 
                and publishing their very own books!
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* --- SECTION 2: MEET THE AUTHORS GALLERY --- */}
      <section className="py-5 bg-chalkboard">
        <Container className="py-5">
          <div className="text-center mb-5 invisible-start" ref={addToRefs}>
            <h2 className="fun-heading display-4 chalk-text mb-3">Our Shining Stars 🌟</h2>
            <p className="chalk-text fs-5" style={{ opacity: 0.9 }}>
              Take a look at the amazing books created by our talented students!
            </p>
          </div>

          <Row className="g-4 justify-content-center">
            {authors.map((author, index) => (
              <Col xs={12} sm={6} lg={4} key={index} className="invisible-start" ref={addToRefs}>
                <Card className="kid-card h-100" style={{ borderColor: author.color }}>
                  <Card.Img variant="top" src={author.img} className="kid-img" alt={author.book} />
                  <Card.Body className="text-center p-4">
                    <h4 className="fun-heading mb-3" style={{ color: '#2f3542', fontSize: '1.2rem' }}>
                      "{author.book}"
                    </h4>
                    <span 
                      className="d-inline-block px-3 py-1 rounded-pill" 
                      style={{ backgroundColor: `${author.color}20`, color: author.color, fontWeight: 'bold' }}
                    >
                      {author.tag}
                    </span>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* --- SECTION 3: THE JOURNEY --- */}
      <section className="py-5" style={{ backgroundColor: '#fffbe3' }}>
        <Container className="py-5">
          <Row className="align-items-center">
            <Col lg={6} className="invisible-start mb-4 mb-lg-0" ref={addToRefs}>
              <h2 className="fun-heading display-4 mb-4" style={{ color: '#ff6b81' }}>The Journey 🚀</h2>
              <p className="text-reading mb-3">
                Our <strong>Author to Entrepreneur</strong> program takes students on an exciting adventure. 
                They don't just write a story; they learn how to structure it, illustrate it, and understand 
                what it takes to bring a real product to the world.
              </p>
              <ul className="text-reading" style={{ listStyleType: 'none', paddingLeft: 0 }}>
                <li className="mb-2">✏️ <strong>Step 1:</strong> Brainstorming & Storyboarding</li>
                <li className="mb-2">🎨 <strong>Step 2:</strong> Illustrating the magic</li>
                <li className="mb-2">📖 <strong>Step 3:</strong> Publishing the final book</li>
                <li className="mb-2">💼 <strong>Step 4:</strong> Learning to share it like an entrepreneur!</li>
              </ul>
            </Col>
            <Col lg={6} className="invisible-start" ref={addToRefs}>
              {/* Harika bir okul kolajı görünümü */}
              <div className="p-3 bg-white rounded-4 shadow-lg" style={{ transform: 'rotate(2deg)' }}>
                <Row className="g-2">
                  <Col xs={6}><img src="/schoolproject/SchoolProjectsLittleHibba4.png" alt="Student Work" className="img-fluid rounded-3" /></Col>
                  <Col xs={6}><img src="/schoolproject/SchoolProjectsLittleHibba5.png" alt="Student Work" className="img-fluid rounded-3" /></Col>
                </Row>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* --- SECTION 4: CALL TO ACTION --- */}
      <section className="py-5 bg-school-pattern text-center">
        <Container className="py-5 invisible-start" ref={addToRefs}>
          <h2 className="fun-heading display-4 mb-4" style={{ color: '#1e90ff' }}>Bring This Program To Your School!</h2>
          <p className="text-reading mx-auto mb-5" style={{ maxWidth: '600px' }}>
            Empower your students to become published authors. Let's nurture the next generation of creative thinkers and young entrepreneurs together.
          </p>
          <Link to="/contact">
            <Button size="lg" className="px-5 py-3 rounded-pill shadow-lg border-0 fun-heading" style={{ backgroundColor: '#ff4757', fontSize: '1.2rem' }}>
              Start a School Project 📚
            </Button>
          </Link>
        </Container>
      </section>
    </>
  );
};

export default LittleAuthors;